import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Table,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconArrowDown,
  IconArrowUp,
  IconChevronLeft,
  IconChevronRight,
  IconSearch,
} from '@tabler/icons-react';
import { useEffect, useState, type ReactNode } from 'react';
import { sporocilo } from '../api/napaka';
import { stevilo } from '../oblika/oblika';
import { PomocIkona } from '../pomoc/PomocIkona';
import { barve } from '../tema';
import { VELIKOSTI, type OsnovaSeznama, type StanjeSeznama } from './useSeznam';

/**
 * Naslov zaslona (h1) z opisom in dejanji desno (npr. »Nov kupec«); {@code pomoc} je id članka pomoči za »?« ob
 * naslovu.
 */
export function GlavaZaslona({
  naslov,
  opis,
  dejanja,
  pomoc,
}: {
  naslov: string;
  opis?: string;
  dejanja?: ReactNode;
  pomoc?: string;
}) {
  return (
    <Group justify="space-between" mb="md" align="flex-end" wrap="wrap" gap="sm">
      <div>
        <Group gap={4} wrap="nowrap">
          <Title order={1} size="h2">
            {naslov}
          </Title>
          {pomoc && <PomocIkona clanek={pomoc} />}
        </Group>
        {opis && (
          <Text size="sm" c="dimmed">
            {opis}
          </Text>
        )}
      </div>
      {dejanja}
    </Group>
  );
}

/** Iskanje na strežniku: počaka, da uporabnik neha tipkati, Enter takoj. */
export function IskalnoPolje({ seznam, namig }: { seznam: OsnovaSeznama; namig: string }) {
  const [vnos, setVnos] = useState(seznam.iskanje);
  const [zakasnjen] = useDebouncedValue(vnos, 400);
  useEffect(() => {
    if (zakasnjen.trim() !== seznam.iskanje) {
      seznam.nastavi({ q: zakasnjen.trim() || null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- samo ob spremembi vnosa
  }, [zakasnjen]);
  return (
    <TextInput
      aria-label="Iskanje"
      placeholder={namig}
      leftSection={<IconSearch size={16} />}
      value={vnos}
      onChange={(e) => setVnos(e.currentTarget.value)}
      onKeyDown={(e) => e.key === 'Enter' && seznam.nastavi({ q: vnos.trim() || null })}
      style={{ flex: '1 1 320px' }}
    />
  );
}

/** Glava stolpca: klik razvršča na strežniku (naraščajoče → padajoče → privzeto). */
export function Stolpec<R extends string>({
  seznam,
  kljuc,
  naziv,
  desno = false,
}: {
  seznam: StanjeSeznama<R>;
  kljuc?: R;
  naziv: string;
  desno?: boolean;
}) {
  const izbran = kljuc !== undefined && seznam.razvrsti === kljuc;
  const besedilo = (
    <Text component="span" fw={700} size="11px" c={barve.besediloSekundarno} style={{ letterSpacing: 0.5 }}>
      {naziv.toUpperCase()}
    </Text>
  );
  return (
    <Table.Th
      style={{ textAlign: desno ? 'right' : undefined, whiteSpace: 'nowrap' }}
      aria-sort={izbran ? (seznam.smer === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      {kljuc === undefined ? (
        besedilo
      ) : (
        <UnstyledButton
          onClick={() => seznam.razvrscaj(kljuc)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          {besedilo}
          {izbran && (seznam.smer === 'asc' ? <IconArrowUp size={12} /> : <IconArrowDown size={12} />)}
        </UnstyledButton>
      )}
    </Table.Th>
  );
}

/**
 * Seznam po straneh: filtri, tabela (na telefonu se vodoravno pomika v svojem okvirju), napaka z »Poskusi znova«,
 * prazno stanje in strani (velikost, »1–25 od N«, ‹ ›).
 */
export function Seznam({
  seznam,
  filtri,
  glava,
  vrstice,
  stVrstic,
  stStolpcev,
  skupaj,
  nalaganje,
  osvezevanje,
  napaka,
  ponovi,
  prazno,
  napakaNaslov,
  minSirina = 900,
  nadTabelo,
}: {
  seznam: OsnovaSeznama;
  filtri?: ReactNode;
  glava: ReactNode;
  vrstice: ReactNode;
  stVrstic: number;
  stStolpcev: number;
  skupaj: number;
  nalaganje: boolean;
  osvezevanje: boolean;
  napaka: unknown;
  ponovi: () => void;
  prazno: string;
  napakaNaslov: string;
  minSirina?: number;
  nadTabelo?: ReactNode;
}) {
  const strani = Math.max(1, Math.ceil(skupaj / seznam.velikost));
  const { zamik, velikost, stran } = seznam;
  return (
    <Paper radius="lg" p={{ base: 'sm', sm: 'lg' }} shadow="xs">
      {filtri && (
        <Group gap="sm" mb="md" align="flex-end" wrap="wrap">
          {filtri}
        </Group>
      )}
      {nadTabelo}
      {napaka ? (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} title={napakaNaslov}>
          <Text size="sm">{sporocilo(napaka)}</Text>
          <Button size="xs" variant="light" color="red" mt="sm" onClick={ponovi}>
            Poskusi znova
          </Button>
        </Alert>
      ) : (
        <Table.ScrollContainer minWidth={minSirina}>
          <Table striped highlightOnHover verticalSpacing="xs" className="stevilke" aria-busy={osvezevanje}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={50}>
                  <Text fw={700} size="11px" c={barve.besediloSekundarno}>
                    #
                  </Text>
                </Table.Th>
                {glava}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {vrstice}
              {!nalaganje && stVrstic === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={stStolpcev + 1}>
                    <Text ta="center" c="dimmed" py="xl">
                      {prazno}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
          {nalaganje && (
            <Group justify="center" py="xl">
              <Loader size="sm" />
            </Group>
          )}
        </Table.ScrollContainer>
      )}

      <Group justify="space-between" mt="md" wrap="wrap" gap="sm">
        <Group gap="xs">
          <Text size="sm" c="dimmed">
            Prikaži
          </Text>
          <Select
            aria-label="Velikost strani"
            data={VELIKOSTI}
            value={String(velikost)}
            onChange={(v) => seznam.nastavi({ velikost: v === '25' ? null : v })}
            allowDeselect={false}
            w={80}
            size="sm"
          />
          <Text size="sm" c="dimmed">
            na stran
          </Text>
        </Group>
        <Group gap="xs">
          <Text size="sm" c="dimmed" data-testid="obseg">
            {skupaj === 0 ? '0 od 0' : `${zamik + 1}–${Math.min(zamik + velikost, skupaj)} od ${stevilo(skupaj)}`}
          </Text>
          <ActionIcon
            variant="default"
            size="lg"
            aria-label="Prejšnja stran"
            disabled={stran <= 1}
            onClick={() => seznam.listaj(-1, strani)}
          >
            <IconChevronLeft size={18} />
          </ActionIcon>
          <Text size="sm">
            Stran <b>{stran}</b> / {strani}
          </Text>
          <ActionIcon
            variant="default"
            size="lg"
            aria-label="Naslednja stran"
            disabled={stran >= strani}
            onClick={() => seznam.listaj(1, strani)}
          >
            <IconChevronRight size={18} />
          </ActionIcon>
        </Group>
      </Group>
    </Paper>
  );
}

/** Ovoj zaslona s seznamom (največja širina, sredina). */
export function ZaslonSeznama({ children }: { children: ReactNode }) {
  return (
    <Box maw={1400} mx="auto">
      {children}
    </Box>
  );
}
